// src/components/managed-sources/FacebookAuthStep.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandFacebook,
  IconExternalLink,
  IconInfoCircle,
  IconPlugConnected,
  IconX,
} from "@tabler/icons-react";

import {
  getFacebookAuthUrl,
  exchangeFacebookToken,
  listFacebookAdAccounts,
} from "@/api/managedSources";
import { listAuthorizations } from "@/api/authorizations";
import { useFacebookOAuthPopup } from "@/hooks/useFacebookOAuthPopup";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  authorizationRenderOption,
  authorizationSelectData,
  isUsableByMe,
} from "@/components/authorizations/authorizationOwnership";
import type {
  ConnectorInstanceIdentifier,
  FacebookAdAccount,
} from "@/types/managedSources";

/** Same canonical form the backend stores (`act_<digits>`), so a manually
 * typed `123` or `ACT_123` matches a connected `act_123`. */
function canonicalAdAccountId(raw: string): string {
  const v = raw.trim().toLowerCase();
  if (!v) return "";
  return v.startsWith("act_") ? v : `act_${v}`;
}

type Props = {
  authorizationId: string | null;
  adAccountId: string;
  onAuthorizationChange: (id: string | null) => void;
  onAdAccountChange: (id: string) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
  /** Ad accounts already connected as sources in this Verity account —
   * greyed out with an "Already connected" badge, and never valid to pick. */
  connectedInstances?: ConnectorInstanceIdentifier[];
  /** Lifts the selected account's display metadata (name, currency) to the
   * wizard so it can be persisted with the source. Null when the selected id
   * isn't in the discovered list (manual entry). */
  onAdAccountMetaChange?: (meta: FacebookAdAccount | null) => void;
};

export default function FacebookAuthStep({
  authorizationId,
  adAccountId,
  onAuthorizationChange,
  onAdAccountChange,
  onConnectionNameChange,
  onValidated,
  connectedInstances,
  onAdAccountMetaChange,
}: Props) {
  // Existing authorizations
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Mode
  const [mode, setMode] = useState<"existing" | "new">("existing");

  // New auth form
  const [authName, setAuthName] = useState("");

  // Ad accounts
  const [adAccounts, setAdAccounts] = useState<FacebookAdAccount[]>([]);
  const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);
  const [adAccountError, setAdAccountError] = useState("");

  // Signed state returned by /auth-url; must be echoed back to
  // /exchange-token so the backend can verify the handoff.
  const stateRef = useRef<string | null>(null);

  const connectedSet = useMemo(
    () =>
      new Set(
        (connectedInstances ?? []).map((i) => canonicalAdAccountId(i.value)),
      ),
    [connectedInstances],
  );
  const isConnected = useCallback(
    (id: string) => connectedSet.has(canonicalAdAccountId(id)),
    [connectedSet],
  );

  const handleCode = useCallback(
    async ({ code, state }: { code: string; state: string }) => {
      const result = await exchangeFacebookToken(code, authName.trim(), state);
      onAuthorizationChange(result.authorization_id);
      if (result.ad_accounts?.length) {
        setAdAccounts(result.ad_accounts);
        // Never auto-select an account that is already connected — it can't
        // be submitted, so pre-filling it would only pre-fill an error.
        if (
          result.ad_accounts.length === 1 &&
          !isConnected(result.ad_accounts[0].ad_account_id)
        ) {
          onAdAccountChange(result.ad_accounts[0].ad_account_id);
        }
      }
    },
    [authName, onAuthorizationChange, onAdAccountChange, isConnected],
  );

  const [localAuthError, setLocalAuthError] = useState("");

  const {
    openPopup,
    loading: oauthLoading,
    error: hookError,
  } = useFacebookOAuthPopup({
    getExpectedState: () => stateRef.current,
    onCode: handleCode,
  });

  const oauthError = hookError || localAuthError || "";

  // Load existing Facebook authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const auths = await listAuthorizations({ type: "FACEBOOK" });
        setExistingAuths(auths);
        // Only rows I may use count (ADR-0329): a list of colleagues'
        // personal connections alone starts on "Create new connection".
        if (!auths.some(isUsableByMe)) {
          setMode("new");
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Validate when authorization + ad account are set. An already-connected
  // account is never valid — the backend would 409 it at submit.
  const selectedIsConnected = !!adAccountId.trim() && isConnected(adAccountId);
  useEffect(() => {
    onValidated(!!authorizationId && !!adAccountId.trim() && !selectedIsConnected);
  }, [authorizationId, adAccountId, selectedIsConnected, onValidated]);

  // Lift the selected account's display metadata (name, currency) so the
  // wizard can persist it with the source; null for manual entries.
  useEffect(() => {
    if (!onAdAccountMetaChange) return;
    const canonical = canonicalAdAccountId(adAccountId);
    const meta =
      adAccounts.find(
        (a) => canonicalAdAccountId(a.ad_account_id) === canonical,
      ) || null;
    onAdAccountMetaChange(canonical ? meta : null);
  }, [adAccountId, adAccounts, onAdAccountMetaChange]);

  // Load ad accounts when authorization is set
  useEffect(() => {
    if (!authorizationId) {
      setAdAccounts([]);
      return;
    }
    async function loadAdAccounts() {
      setLoadingAdAccounts(true);
      setAdAccountError("");
      try {
        const result = await listFacebookAdAccounts(authorizationId!);
        setAdAccounts(result.ad_accounts);
        // Auto-select if only one account — unless it's already connected
        // (see handleCode: pre-filling a 409 helps nobody).
        if (
          result.ad_accounts.length === 1 &&
          !isConnected(result.ad_accounts[0].ad_account_id)
        ) {
          onAdAccountChange(result.ad_accounts[0].ad_account_id);
        }
      } catch (e: any) {
        setAdAccountError(e?.message || "Failed to load Facebook ad accounts");
      } finally {
        setLoadingAdAccounts(false);
      }
    }
    loadAdAccounts();
  }, [authorizationId]);

  async function handleConnectFacebook() {
    if (!authName.trim()) return;
    setLocalAuthError("");
    try {
      const { url, state } = await getFacebookAuthUrl();
      stateRef.current = state;
      openPopup(url);
    } catch (e: any) {
      stateRef.current = null;
      setLocalAuthError(e?.message || "Failed to get authorization URL");
    }
  }

  function handleSelectExistingAuth(authId: string | null) {
    onAuthorizationChange(authId);
    // Reset ad account when switching authorization
    onAdAccountChange("");
  }

  const existingAuthOptions = authorizationSelectData(existingAuths);

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconBrandFacebook size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Facebook Ads
          </Text>
          <Text size="sm" c="dimmed">
            Link your Facebook ad account to start syncing campaign data
            automatically.
          </Text>
        </Stack>
      </Group>

      {/* Mode selector */}
      {existingAuths.length > 0 && (
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as "existing" | "new");
            if (v === "new") {
              onAuthorizationChange(null);
              onAdAccountChange("");
            }
          }}
          data={[
            { label: "Use existing connection", value: "existing" },
            { label: "Create new connection", value: "new" },
          ]}
          fullWidth
        />
      )}

      {/* Existing authorization */}
      {mode === "existing" && existingAuths.length > 0 && (
        <Stack gap="md">
          <Select
            label="Facebook authorization"
            placeholder="Select a saved connection"
            data={existingAuthOptions}
            renderOption={authorizationRenderOption(existingAuths)}
            value={authorizationId}
            onChange={handleSelectExistingAuth}
            size="md"
          />
        </Stack>
      )}

      {/* New authorization */}
      {mode === "new" && (
        <Stack gap="md">
          <TextInput
            label="Connection name"
            placeholder="e.g. My Facebook Ads"
            value={authName}
            onChange={(e) => {
              setAuthName(e.currentTarget.value);
              onConnectionNameChange?.(e.currentTarget.value);
            }}
            required
            size="md"
            disabled={!!authorizationId}
          />

          {!authorizationId && (
            <Button
              variant="outline"
              leftSection={<IconBrandFacebook size={18} />}
              rightSection={<IconExternalLink size={14} />}
              onClick={handleConnectFacebook}
              loading={oauthLoading}
              disabled={!authName.trim()}
              size="md"
              fullWidth
            >
              Connect with Facebook
            </Button>
          )}

          {oauthError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {oauthError}
            </Alert>
          )}
        </Stack>
      )}

      {/* Ad account selector (shown after auth) */}
      {authorizationId && (
        <>
          {loadingAdAccounts ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Loading Facebook ad accounts...
              </Text>
            </Group>
          ) : adAccountError ? (
            <Alert color="red" icon={<IconX size={16} />}>
              {adAccountError}
            </Alert>
          ) : adAccounts.length > 0 ? (
            <Select
              label="Ad Account"
              placeholder="Select an ad account"
              data={adAccounts.map((a) => ({
                value: a.ad_account_id,
                label: a.account_name
                  ? `${a.account_name} (${a.ad_account_id})`
                  : a.ad_account_id,
                disabled: isConnected(a.ad_account_id),
              }))}
              renderOption={({ option }) => (
                <Group flex="1" gap="xs" justify="space-between" wrap="nowrap">
                  <span>{option.label}</span>
                  {isConnected(option.value) && (
                    <Badge size="xs" variant="light" color="gray">
                      Already connected
                    </Badge>
                  )}
                </Group>
              )}
              value={adAccountId || null}
              onChange={(v) => onAdAccountChange(v || "")}
              size="md"
              searchable
            />
          ) : (
            <TextInput
              label="Ad Account ID"
              placeholder="act_123456789"
              description="No accounts found automatically. Enter the ad account ID manually."
              value={adAccountId}
              onChange={(e) => onAdAccountChange(e.currentTarget.value)}
              size="md"
            />
          )}
        </>
      )}

      {/* Manually-typed (or stale) id that is already connected: the Select
          blocks picking these, but the free-text fallback can't. */}
      {authorizationId && selectedIsConnected && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
          This ad account is already connected as a data source in this
          account. Pick a different ad account.
        </Alert>
      )}

      {/* Connected confirmation */}
      {authorizationId && !!adAccountId.trim() && !selectedIsConnected && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                Ad Account:{" "}
                <Text span fw={500} ff="monospace">
                  {adAccountId}
                </Text>
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
